import { type ContractDto, type ContractPaperStatusDto } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconMail, IconSignature } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { ask } from '../../confirm';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';

/**
 * QA P4: umowa / aneks wydany do podpisu papierowego.
 * Dział umów: „Wyślij klientowi” (PDF w załączniku, prośba: wydrukuj, podpisz, odeślij).
 * Opiekun / handlowiec (bez prawa wydawania): „Klient chce podpis elektroniczny” — prośba do działu umów.
 */
export function PaperSignPanel({ k, onEsign }: { k: ContractDto; onEsign?: () => void }) {
  const qc = useQueryClient();
  const paperIssued = k.status === 'wydany' && k.issueMode !== 'epodpis' && k.basis !== 'oferta';
  const q = useQuery({ queryKey: ['contract-paper', k.id, k.status], queryFn: () => api<ContractPaperStatusDto>(`/contracts/${k.id}/paper`), enabled: paperIssued });
  const [wish, setWish] = useState(false);
  const [note, setNote] = useState('');
  const done = (d: ContractPaperStatusDto) => {
    qc.setQueryData(['contract-paper', k.id, k.status], d);
    void qc.invalidateQueries({ queryKey: ['case-timeline'] });
  };
  const send = useMutation({
    mutationFn: () => api<ContractPaperStatusDto>(`/contracts/${k.id}/paper/send`, { method: 'POST' }),
    onSuccess: (d) => (notifyOk(`Wysłano klientowi ${k.kind === 'aneks' ? 'aneks' : 'umowę'} do podpisu (${d.lastSent?.to ?? ''})`), done(d)),
    onError: notifyError,
  });
  const request = useMutation({
    mutationFn: () => api<ContractPaperStatusDto>(`/contracts/${k.id}/paper/esign-request`, { method: 'POST', body: { note } }),
    onSuccess: (d) => (notifyOk('Przekazano działowi umów: klient chce podpis elektroniczny'), done(d), setWish(false)),
    onError: notifyError,
  });
  const s = q.data;
  if (!paperIssued || !s) return null;
  const doc = k.kind === 'aneks' ? 'aneks' : 'umowę';
  if (!s.can.send && !s.can.requestEsign && !s.esignRequest) return null;
  return (
    <>
      {s.esignRequest && (
        <Alert color="orange" variant="light" mb="md" icon={<IconSignature size={18} />} title="Klient chce podpis elektroniczny">
          <Text size="sm">
            Przekazał(a) {s.esignRequest.byName ?? '—'}, {formatDateTime(s.esignRequest.at)}
            {s.esignRequest.note ? ` · ${s.esignRequest.note}` : ''}.
          </Text>
          {s.can.send && onEsign && (
            <Button size="xs" mt="xs" leftSection={<IconSignature size={14} />} onClick={onEsign}>
              Wyślij do podpisu elektronicznego
            </Button>
          )}
        </Alert>
      )}
      <Group gap="xs" mb="md" wrap="wrap">
        {s.can.send && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconMail size={14} />}
            loading={send.isPending}
            disabled={!s.to}
            title={s.to ? undefined : 'Klient nie ma adresu e-mail — uzupełnij go w karcie klienta'}
            onClick={() =>
              void ask(`E-mail do ${s.to} z PDF w załączniku i prośbą: wydrukuj, podpisz, odeślij. Wpis w historii sprawy.`, { title: `Wysłać ${doc} klientowi?`, confirm: 'Wyślij' }).then((ok) => ok && send.mutate())
            }
          >
            Wyślij klientowi
          </Button>
        )}
        {s.can.requestEsign && !s.esignRequest && (
          <Button size="xs" variant="default" leftSection={<IconSignature size={14} />} onClick={() => setWish(true)}>
            Klient chce podpis elektroniczny
          </Button>
        )}
        {s.lastSent ? (
          <Text size="xs" c="dimmed">
            Wysłano klientowi {formatDateTime(s.lastSent.at)} ({s.lastSent.to}){s.lastSent.byName ? ` · ${s.lastSent.byName}` : ''}
          </Text>
        ) : (
          s.can.send && !s.to && (
            <Text size="xs" c="orange">
              Klient nie ma adresu e-mail — uzupełnij go w karcie klienta
            </Text>
          )
        )}
      </Group>
      <Modal opened={wish} onClose={() => setWish(false)} title="Klient chce podpis elektroniczny">
        <Stack>
          <Text size="sm">Dział umów dostanie powiadomienie i wyda {doc} ponownie do podpisu elektronicznego. Wpis zostanie w historii sprawy.</Text>
          <Textarea label="Uwagi (opcjonalnie)" placeholder="np. klient podpisze w tym tygodniu, kod na telefon" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setWish(false)}>
              Anuluj
            </Button>
            <Button loading={request.isPending} onClick={() => request.mutate()}>
              Przekaż działowi umów
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
